import { z } from '../utils/zodConfig';

/**
 * Zod schema for payout with conditional validation
 * Validates complete payout data including transaction type and conditional fields
 */
export const payoutSchema = z.object({
  id: z.string().optional(),

  date: z
    .string()
    .min(1, 'La date est requise')
    .refine(
      (date) => !isNaN(Date.parse(date)),
      'Format de date invalide'
    ),

  type: z.enum(['payout', 'achat', 'stacking', 'vente', 'swap', 'transfert'], {
    message: 'Choisissez un type de transaction valide',
  }),

  // Simple transaction fields (for non-swap transactions)
  crypto: z.object({
    id: z.string(),
    symbol: z.string(),
    name: z.string(),
    source: z.enum(['coingecko', 'livecoinwatch']).optional(),
  }).optional(),

  amount: z.number().optional(),
  unitPrice: z.number().optional(),
  purchaseAmount: z.number().optional(),

  // Swap transaction fields
  sentCrypto: z.object({
    id: z.string(),
    symbol: z.string(),
    name: z.string(),
    source: z.enum(['coingecko', 'livecoinwatch']).optional(),
  }).optional(),

  sentAmount: z.number().optional(),
  sentUnitPrice: z.number().optional(),

  receivedCrypto: z.object({
    id: z.string(),
    symbol: z.string(),
    name: z.string(),
    source: z.enum(['coingecko', 'livecoinwatch']).optional(),
  }).optional(),

  receivedAmount: z.number().optional(),
  receivedUnitPrice: z.number().optional(),

  // Common optional fields
  transactionHash: z.string().optional(),
  notes: z.string().optional(),
  fees: z.number().min(0).optional(),
  platform: z.string().optional(),

  // Case 212 — valeur portefeuille en € à la date de cession (vente/swap)
  portfolioValueAtSale: z.number().min(0).optional(),

  // Calculated fields (optional as they're computed)
  totalValue: z.number().optional(),
  sentTotalValue: z.number().optional(),
  receivedTotalValue: z.number().optional(),

}).refine(
  (data) => {
    // If type is 'swap', swap fields must be provided
    if (data.type === 'swap') {
      return !!data.sentCrypto && !!data.sentAmount && !!data.sentUnitPrice &&
             !!data.receivedCrypto && !!data.receivedAmount && !!data.receivedUnitPrice;
    }
    // If type is not 'swap', simple transaction fields must be provided
    return !!data.crypto && !!data.amount && (Number.isFinite(data.unitPrice) || Number.isFinite(data.purchaseAmount));
  },
  {
    message: 'Les champs requis pour le type de transaction sélectionné doivent être renseignés',
    path: ['type'],
  }
).refine(
  (data) => {
    // For swap transactions, ensure sent and received cryptos are different
    if (data.type === 'swap' && data.sentCrypto && data.receivedCrypto) {
      return data.sentCrypto.id !== data.receivedCrypto.id;
    }
    return true;
  },
  {
    message: 'Les cryptomonnaies envoyée et reçue doivent être différentes',
    path: ['receivedCrypto'],
  }
);

export type PayoutFormData = z.infer<typeof payoutSchema>;
